import { useMutation, useQueryClient } from 'react-query';
import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import templateApi from '../services/templateApi';
import { extractMessageFromError } from '../utils/backEndErrorMessages';
import { Signature } from '../types/signature';
import { AxiosError } from 'axios';
import { BaseQueryKey } from './useTemplate';
import { BaseQueryKey as TemplatesQueryKey } from './useTemplates';

export const useMakeTemplateEffective = (templateUUID: string) => {
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation(
    (signaturePayload: Signature) => templateApi.makeTemplateEffective(companyId, templateUUID, signaturePayload),
    {
      onSuccess: async () => {
        showToast({
          description: t('template.makeEffective.success'),
          status: 'success',
          title: t('common.toast.success'),
          'data-cy': 'toast-make-effective-success',
        });
        await queryClient.invalidateQueries([TemplatesQueryKey]);
        await queryClient.invalidateQueries([BaseQueryKey]);
      },
      onError: (error: AxiosError) => {
        const errorMessage = extractMessageFromError(error);
        if (
          errorMessage === 'Invalid email or password' ||
          errorMessage === 'Email and/or password do not match currently signed in user'
        ) {
          showToast({
            description: t('template.makeEffective.errors.invalidCredentials'),
            status: 'error',
            title: t('template.makeEffective.errors.incorrectCredentialsTitle'),
            'data-cy': 'toast-make-effective-invalid-credentials',
          });
        } else {
          showToast({
            description: `${t('template.makeEffective.errors.failed')}${errorMessage}`,
            status: 'error',
            title: t('common.toast.error'),
            'data-cy': 'toast-make-effective-error',
          });
        }
      },
    },
  );
};
